import { Component, ChangeDetectorRef, OnInit, OnDestroy, Inject, ViewChild } from '@angular/core';
import { AccountPaymentService, IFirebaseAddress } from '@ztarmobile/zwp-service-backend';
import { Validators, FormGroup, FormControl, AbstractControl, ValidationErrors } from '@angular/forms';
import { PlatformLocation } from '@angular/common';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { AccountPaymentProfile, IPaymentAddress } from '@ztarmobile/zwp-service-backend-v2';
import { AddressLookupComponent } from '@widgets/address-lookup/address-lookup.component';

export class CreditCardContext {
  public paymentMethod: AccountPaymentProfile;
  public title: string;
  public note: string;
  public noteLinkText: string;
  public customClass?: string;
}

@Component({
  selector: 'app-edit-cc-modal',
  templateUrl: './edit-cc-modal.component.html'
})
export class EditCcModalComponent implements OnInit, OnDestroy {
  @ViewChild('addressLookup') addressLookup: AddressLookupComponent;
  public context: CreditCardContext;
  public billingAddress: IFirebaseAddress;
  public expirationYearRange: Array<number>;
  public months: { value: string; label: string }[] = [];
  public ccForm: FormGroup;
  public displayedAddressModel: any;

  public resetAddressForm: boolean;
  private addressEdited = false;

  constructor(@Inject(MAT_DIALOG_DATA) public data: any, public dialog: MatDialogRef<CreditCardContext>, private cdRef: ChangeDetectorRef,
    private location: PlatformLocation) {
    this.context = data;
    location.onPopState(() => { this.beforeClose(); this.dialog.close(); });

    this.ccForm = new FormGroup({
      expiry: new FormControl('', [
        Validators.required,
        Validators.pattern(/^(0[1-9]|1[0-2])\/\d{2}$/),
        this.expiryDateValidator
      ])
    });
    const cardProfile = this.context?.paymentMethod?.card_profile;

    this.ccForm.controls.expiry.setValue(cardProfile?.expiry_month + '/' + cardProfile?.expiry_year?.slice(-2));
    this.expirationYearRange = [];

    this.mapBillingAddress(cardProfile?.billing_address)
  }

  ngOnInit(): void {
    this.cdRef.detectChanges();
  }

  ngOnDestroy(): void {
    this.cdRef.detach();
  }

  mapBillingAddress(billingAddress: IPaymentAddress) {
    this.billingAddress = {} as IFirebaseAddress;
    this.billingAddress.address1 = billingAddress.line1;
    this.billingAddress.address2 = billingAddress.line2;
    this.billingAddress.city = billingAddress.city;
    this.billingAddress.state = billingAddress.state;
    this.billingAddress.postalCode = billingAddress.postal_code;
  }

  private expiryDateValidator(control: AbstractControl): ValidationErrors | null {
    const value = control.value;
    if (!value) return null;

    const [month, year] = value.split('/').map(Number);
    if (!month || !year) return { invalidFormat: true };

    const fullYear = 2000 + year;

    const expiry = new Date(fullYear, month - 1, 1);

    const now = new Date();
    const currentMonth = new Date(now.getFullYear(), now.getMonth(), 1);

    return null;
  }

  onExpiryInput(event: Event) {
    const input = event.target as HTMLInputElement;
    let value = input.value.replace(/\D/g, '');

    if (value.length >= 3) {
      value = value.slice(0, 2) + '/' + value.slice(2, 4);
    }

    input.value = value;
    this.ccForm.controls.expiry.setValue(value, { emitEvent: false });
  }

  beforeClose(): boolean {
    if (document.body.classList.contains('modal-open')) {
      document.body.classList.remove('modal-open');
    }
    return false;
  }

  public savePaymentInfo(): void {
    this.ccForm.markAllAsTouched();
    this.addressLookup.addressFieldsForm.markAllAsTouched();

    if (this.ccForm.pristine && this.addressLookup.addressFieldsForm.pristine) {
      this.closeDialog();
      return;
    }
    const newAddress: IPaymentAddress = {
      state: this.billingAddress.state,
      city: AccountPaymentService.shortenAddress(this.billingAddress.city, 20),
      line1: AccountPaymentService.shortenAddress(this.billingAddress.address1, 30),
      postal_code: this.billingAddress.postalCode,
      country: 'US'
    }

    if (this.addressLookup.addressFieldsForm.valid && this.ccForm.valid) {
      if (!!this.addressEdited) {
        Object.assign(this.context.paymentMethod.card_profile.billing_address, newAddress);
      }
      const expirationDate = this.ccForm.get('expiry').value.split('/');
      this.context.paymentMethod.card_profile.expiry_month = expirationDate[0];
      this.context.paymentMethod.card_profile.expiry_year = '20' + expirationDate[1];
      this.closeDialog({ paymentMethod: this.context.paymentMethod });
    }
  }

  public closeDialog(action?: any): void {
    this.beforeClose();
    this.dialog.close(action);
  }

  public addressLookUpChanged(address: IFirebaseAddress, isEdit?: boolean): void {
    this.billingAddress = address;
    this.addressEdited = true;
  }
}
